import { Session } from './session';

export type ConfigurationView = 'Home' | 'Models' | 'Agents' | 'Skills' | 'Teams' | 'GitHub Apps' | 'Environments' | 'Jobs' | 'Schedules' | 'Usage' | 'Chat';

export type NavGroup = {
  label: string;
  items: ConfigurationView[];
};

export const navGroups: NavGroup[] = [
  { label: 'Build', items: ['Agents', 'Skills', 'Models'] },
  { label: 'Access', items: ['Teams', 'GitHub Apps'] },
  { label: 'Operations', items: ['Chat', 'Jobs', 'Schedules'] },
  { label: 'Insights', items: ['Usage', 'Environments'] },
];

export const configurationItems: ConfigurationView[] = ['Home', ...navGroups.flatMap(group => group.items)];

export const defaultConfigurationView: ConfigurationView = 'Home';

const adminOnlyViews: ConfigurationView[] = ['GitHub Apps', 'Usage'];

const adminWriteViews: ConfigurationView[] = ['Models', 'Teams'];

export function canViewConfigurationView(session: Session, view: ConfigurationView): boolean {
  return session.isAdmin || !adminOnlyViews.includes(view);
}

export function canEditConfigurationView(session: Session, view: ConfigurationView): boolean {
  return session.isAdmin || !adminWriteViews.includes(view);
}

export function getNavGroups(session: Session): NavGroup[] {
  return navGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => canViewConfigurationView(session, item)),
    }))
    .filter(group => group.items.length > 0);
}

export function getDefaultConfigurationView(session: Session): ConfigurationView {
  return 'Home';
}

export const configurationViewPaths: Record<ConfigurationView, string> = {
  Home: '/',
  Agents: '/agents',
  Chat: '/chat',
  Environments: '/environments',
  'GitHub Apps': '/github-apps',
  Jobs: '/jobs',
  Models: '/models',
  Schedules: '/schedules',
  Usage: '/usage',
  Skills: '/skills',
  Teams: '/teams',
};

const configurationViewLabels: Partial<Record<ConfigurationView, string>> = {
  'GitHub Apps': 'GitHub connections',
};

export function getConfigurationViewLabel(view: ConfigurationView): string {
  return configurationViewLabels[view] ?? view;
}

export function getNavGroupLabel(view: ConfigurationView): string {
  if (view === 'Home') {
    return 'Overview';
  }

  return navGroups.find(group => group.items.includes(view))?.label ?? 'Configuration';
}

export function getConfigurationViewPath(view: ConfigurationView): string {
  return configurationViewPaths[view];
}

const githubAppStageAliases: Record<string, string> = {
  development: 'dev',
  integration: 'int',
  consolidation: 'cons',
  production: 'prod',
  local: 'dev',
};

// Takes the active stage rather than the build's own: there are four separate GitHub Apps
// (@ACA-DEV, @ACA-INT, @ACA-CONS, @ACA-PROD), so one deployment serving every stage would
// otherwise link to the wrong one in three cases out of four.
export function getGitHubAppUrl(stage = process.env.REACT_APP_STAGE): string {
  const normalizedStage = (stage || 'dev').trim().toLowerCase();
  const environment = githubAppStageAliases[normalizedStage] || normalizedStage;

  return `https://github.platform.vwfs.io/github-apps/ai-coding-agent-${encodeURIComponent(environment)}`;
}

export function getConfigurationViewFromPath(pathname: string): ConfigurationView {
  if (pathname === '/' || pathname === '') {
    return 'Home';
  }

  const match = Object.entries(configurationViewPaths).find(([, path]) => pathname === path || pathname.startsWith(`${path}/`));

  return match ? (match[0] as ConfigurationView) : defaultConfigurationView;
}
